import { request } from "./http";

/**
 * 获取工程任务列表（分页）
 * @param {Object} params - 请求参数
 * @param {string} params.projectId - 项目ID
 * @param {number} [params.pageNo=1] - 页码
 * @param {number} [params.pageSize=20] - 每页数量
 * @param {string} [params.keyword=""] - 任务名称关键词（模糊搜索）
 * @param {string} [params.status=""] - 任务状态（模糊搜索）
 * @returns {Promise} 返回任务分页列表数据
 */
export const getEngineeringTaskList = async ({
  projectId,
  pageNo = 1,
  pageSize = 10,
  keyword = "",
  status = "",
} = {}) => {
  const payload = {
    pageNo,
    pageSize,
  };
  const query = [];

  const trimmedKeyword = keyword.trim();
  if (trimmedKeyword) {
    query.push({
      field: "taskName",
      operator: "ilike",
      value: `%${trimmedKeyword}%`,
    });
  }

  const trimmedStatus = `${status || ""}`.trim();
  if (trimmedStatus) {
    query.push({
      field: "status",
      operator: "ilike",
      value: `%${trimmedStatus}%`,
    });
  }

  if (query.length) {
    payload.query = query;
  }

  return request.post(
    `/api/v1/compute/task/pageList?projectId=${encodeURIComponent(projectId || "")}`,
    payload,
  );
};

/**
 * 获取指定项目的任务列表
 * @param {Object} params - 请求参数
 * @param {string} params.projectId - 项目ID
 * @returns {Promise} 返回项目的任务列表
 */
export const getProjectTasks = async ({ projectId } = {}) => {
  return request.get(
    `/api/v1/compute/project/tasks?projectId=${encodeURIComponent(projectId || "")}`,
  );
};

/**
 * 获取全局运行中的任务列表
 * @returns {Promise} 返回所有运行中的任务
 */
export const getGlobalRunningTasks = async () => {
  return request.get("/api/v1/compute/project/runningTasks");
};

/**
 * 获取任务 RTK 坐标数据
 * @param {Object} params - 请求参数
 * @param {string} params.taskId - 任务ID
 * @returns {Promise} 返回 RTK 坐标数组
 */
export const getRtkData = async ({ taskId } = {}) => {
  return request.post("/api/v1/compute/task/getRtkData", {
    taskId,
  });
};

/**
 * 删除工程任务
 * @param {string} taskId - 任务ID
 * @returns {Promise} 返回删除结果
 */
export const deleteEngineeringTask = async (taskId) => {
  return request.post(
    `/api/v1/compute/task/delete?id=${encodeURIComponent(taskId || "")}`,
  );
};

/**
 * 获取工程任务可下载的文件类型列表
 * @param {Object} params - 请求参数
 * @param {string} params.childTaskId - 子任务ID
 * @returns {Promise} 返回可导出的文件类型数组
 */
export const getEngineeringExportTypes = async ({ childTaskId } = {}) => {
  return request.get(
    `/api/v1/compute/task/result/exportType?childTaskId=${encodeURIComponent(childTaskId || "")}`,
  );
};

/**
 * 获取工程任务结果下载链接
 * @param {Object} params - 请求参数
 * @param {string} params.childTaskId - 子任务ID
 * @param {string} params.fileSuffix - 文件后缀（导出格式）
 * @returns {Promise} 返回下载链接URL
 */
export const getEngineeringDownloadUrl = async ({
  childTaskId,
  fileSuffix,
} = {}) => {
  return request.post("/api/v1/compute/task/result/download", {
    childTaskId,
    fileSuffix,
  });
};

/**
 * 获取工程任务PDF报告下载链接
 * @param {Object} params - 请求参数
 * @param {string} params.childTaskId - 子任务ID
 * @returns {Promise} 返回PDF下载链接URL
 */
export const getEngineeringPdfDownloadUrl = async ({ childTaskId } = {}) => {
  return request.post("/api/v1/compute/task/result/downloadPdf", {
    childTaskId,
  });
};

/**
 * 获取工程任务日志下载链接
 * @param {Object} params - 请求参数
 * @param {string} params.childTaskId - 子任务ID
 * @returns {Promise} 返回日志下载链接URL
 */
export const getEngineeringLogUrl = async ({ childTaskId } = {}) => {
  return request.post("/api/v1/compute/task/result/log", {
    childTaskId,
  });
};

export const submitEngineeringTaskFeedback = async ({
  childTaskId,
  feedback = "feedback",
} = {}) => {
  return request.post("/api/v1/compute/task/feedback", {
    childTaskId,
    feedback,
  });
};
